import { ArtistFollow } from "./MeetingFeatures";
import type { Artist, SubscriptionOrder, SubscriptionPlan } from "@kira/contracts";
import { Button } from "@kira/ui";
import { Check, Radio, ShieldCheck, Sparkles, TicketCheck, UsersRound, Zap } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { newOperationKey } from "./api";

const money = new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" });
const compact = new Intl.NumberFormat("zh-CN", { notation: "compact", maximumFractionDigits: 1 });

export function SubscriptionAccess({ focusArtist, artists, plans, busy, onPurchase, onUnsubscribe, onBlock }: {
  focusArtist: Artist;
  artists: Artist[];
  plans: SubscriptionPlan[];
  busy: boolean;
  onPurchase: (artistIds: string[], operationKey: string) => Promise<SubscriptionOrder>;
  onBlock: () => void;
  onUnsubscribe: (artist: Artist) => Promise<void>;
}) {
  const orderedPlans = useMemo(() => [...plans].sort((left, right) => left.artistCount - right.artistCount), [plans]);
  const [planSize, setPlanSize] = useState<1 | 3 | 5>(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([focusArtist.id]);
  const [error, setError] = useState("");
  const operationRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const plan = orderedPlans.find((item) => item.artistCount === planSize);

  function choosePlan(size: 1 | 3 | 5) {
    const candidates = [focusArtist, ...artists.filter((artist) => artist.id !== focusArtist.id)];
    setPlanSize(size);
    setSelectedIds(candidates.slice(0, size).map((artist) => artist.id));
    setError("");
    operationRef.current = null;
  }

  function toggleArtist(artistId: string) {
    if (artistId === focusArtist.id) return;
    setSelectedIds((current) => {
      if (current.includes(artistId)) return current.filter((id) => id !== artistId);
      if (current.length >= planSize) {
        setError(`这个套餐需要选择 ${planSize} 位艺人，请先取消一位。`);
        return current;
      }
      setError("");
      return [...current, artistId];
    });
    operationRef.current = null;
  }

  async function submit() {
    if (!plan || selectedIds.length !== plan.artistCount) {
      setError(`请选择 ${planSize} 位艺人后再开通。`);
      return;
    }
    const fingerprint = JSON.stringify([...selectedIds].sort());
    if (!operationRef.current || operationRef.current.fingerprint !== fingerprint) {
      operationRef.current = { fingerprint, key: newOperationKey("subscription-order") };
    }
    try {
      await onPurchase(selectedIds, operationRef.current.key);
      operationRef.current = null;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "订阅订单没有完成，请重试。");
    }
  }

  return (
    <div className="artist-access">
      <div className="artist-access__scroll">
        <section className="artist-access__hero">
          <img src={focusArtist.coverUrl || focusArtist.avatarUrl} alt={`${focusArtist.stageName} 海报`} />
          <div className="artist-access__wash" />
          <div className="artist-access__identity">
            <small>{focusArtist.groupName || focusArtist.category}</small>
            <h3>{focusArtist.stageName}</h3>
            <p>{focusArtist.bio}</p>
            <div><span><Zap size={13} />热度 {compact.format(focusArtist.heat)}</span><span><Radio size={13} />{compact.format(focusArtist.subscriberCount)} 位订阅者</span></div>
          </div>
          {focusArtist.isSubscribed && focusArtist.subscriptionExpiresAt ? <p className="chat-semantics">当前有效期至 {new Date(focusArtist.subscriptionExpiresAt).toLocaleDateString("zh-CN")}，续订会累加 30 天。</p> : null}
          <span className="artist-access__pass"><TicketCheck size={16} />{focusArtist.isSubscribed ? "订阅中" : "待开通"}</span>
        </section>

        <div className="free-follow-explainer"><div className="artist-relation-actions"><ArtistFollow artist={focusArtist}/><button type="button" onClick={()=>document.getElementById("artist-subscription-plans")?.scrollIntoView({behavior:"smooth",block:"start"})}>{focusArtist.isSubscribed?"续订服务":"付费订阅"}</button></div><p><strong>关注：</strong>在首页查看公开动态，随时可取消。</p><p><strong>付费订阅：</strong>30 天内接收艺人统一广播、发送自己的留言，并加入官方圈发布内容和参与活动。艺人不提供一对一私聊。</p></div>
        <section className="access-plans" id="artist-subscription-plans">
          <header><small>订阅艺人</small><strong>选一位，或同时订阅多位艺人</strong></header>
          <div className="access-plans__tabs">
            {orderedPlans.map((item) => <button type="button" key={item.id} className={planSize === item.artistCount ? "is-active" : ""} onClick={() => choosePlan(item.artistCount)}><span>{item.artistCount} 人</span><strong>{money.format(item.totalCents / 100)}</strong><small>{item.discountLabel} · {item.durationDays} 天</small></button>)}
          </div>
          <div className="access-plan-summary"><Sparkles size={17} /><span><strong>{plan?.artistCount ?? planSize} 人订阅组合</strong>{plan ? `单人原价 ${money.format(plan.unitPriceCents / 100)}，结算页会显示最终金额。` : "正在读取套餐价格…"}</span><b>{plan?.discountLabel ?? "计算中"}</b></div>
        </section>

        <section className="access-artists">
          <header><span>选择艺人</span><b>{selectedIds.length}/{planSize}</b></header>
          <div>{artists.map((artist) => {
            const selected = selectedIds.includes(artist.id);
            const locked = artist.id === focusArtist.id;
            return <button type="button" key={artist.id} className={selected ? "is-selected" : ""} aria-pressed={selected} onClick={() => toggleArtist(artist.id)}><img src={artist.avatarUrl} alt="" /><span><strong>{artist.stageName}</strong><small>{artist.groupName || artist.category}{artist.isSubscribed ? " · 已订阅" : ""}</small></span><i>{selected ? <Check size={14} /> : locked ? <ShieldCheck size={14} /> : null}</i></button>;
          })}</div>
        </section>

        <p className="chat-semantics">拉黑后隐藏艺人、官方圈和聊天；有效期照常计算，解除后恢复未到期权益。</p><button className="artist-block-action" type="button" disabled={busy} onClick={onBlock}>拉黑这位艺人</button>
        <div className="access-notice"><ShieldCheck size={18} /><span><strong>开通前请确认</strong>新订阅有效期 30 天；续订从当前到期日累加 30 天，各艺人分别计算。本次不会产生实际扣款。</span></div>
      </div>
      <footer className="artist-access__dock">
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="access-actions">
          {focusArtist.isSubscribed ? <Button className="k-button--ghost" type="button" disabled={busy} onClick={() => void onUnsubscribe(focusArtist)}>取消当前订阅</Button> : null}
          <Button className="k-button--accent" type="button" disabled={busy || !plan || selectedIds.length !== planSize} onClick={() => void submit()}>{busy ? "正在开通…" : `${focusArtist.isSubscribed ? "续订或升级" : "开通"} · ${plan ? money.format(plan.totalCents / 100) : "校价中"}`}<UsersRound size={17} /></Button>
        </div>
      </footer>
    </div>
  );
}
